//src/types/Store.ts

import { db } from '@/firebase'
import {
  collection,
  CollectionReference,
  QueryDocumentSnapshot,
  Timestamp,
  type DocumentData,
  type SnapshotOptions
} from 'firebase/firestore'
import { convertTimestampsToDates, FirebaseProperties } from './Helper/FirebaseProperties'
import type { DocI } from './Document'
import type {
  CategoryReq,
  ModuleInfo,
  CustomNotification,
  SchedulingSettings
} from './Store/StoreInterfaces'
import { type Address, createAddress } from './Helper/Address'
import { Client, ClientConverter } from './Client'
import { Module } from './Store/StoreEnums'
import { Aircraft, AircraftConverter } from './Store/Aircraft'
import { ScheduledEventConverter, ScheduledEvent } from './Store/Event/Event'
import { Room, RoomConverter } from './Store/Room'
import { Lead, LeadConverter } from './Store/Lead'
import { Equipment, EquipmentConverter } from './Helper/Equipment'
import { FirebaseCollections, FirestorePaths } from '@/utils/firestore/FirestorePaths'
import { StoreAlert, StoreAlertConverter } from './Store/Alert'
import { membershipType, PricedMembershipType } from './Helper/Enums'
import { createWorkOrderSettings, type WorkOrderSettings } from './Store/Aircraft/WorkOrder'

export enum BillingStatus {
  Active,
  Inactive,
  PastDue,
  Canceled,
  Unpaid,
  Trial,
  Free
}

export type StoreBillingInfo = {
  mode?: 'free' | 'stripe'
  manualGrants?: {
    modules?: Record<string, { enabled: boolean; source?: string; grantedBy?: string }>
  }
  stripeCustomerId?: string
  stripeSubscriptionId?: string
  stripePriceId?: string
  stripeConnectAccountId?: string
  stripeConnectChargesEnabled?: boolean
  stripeConnectCardPaymentsActive?: boolean
  stripeConnectTransfersEnabled?: boolean
  stripeConnectPayoutsEnabled?: boolean
  stripeConnectDetailsSubmitted?: boolean
  isCurrent?: boolean
  billingEmail?: string
  billingManagedByUid?: string
  subscriptionStatus?: string
  cancelAtPeriodEnd?: boolean
  currentPeriodStart?: Date | Timestamp | null
  currentPeriodEnd?: Date | Timestamp | null
  lastStripeSyncAt?: Date | Timestamp | null
  lastStripeConnectSyncAt?: Date | Timestamp | null
  subscriptionItems?: StoreBillingSubscriptionItem[]
  entitlements?: StoreBillingEntitlements
}

export type StoreBillingSubscriptionItem = {
  id: string
  priceId: string
  productId: string
  nickname: string
  quantity: number
  unitAmount: number
  currency: string
  interval: string
  amountMonthly: number
}

export type BillingSlotType = 'aircraft' | 'rooms' | 'part141' | 'fuelTrucks'

export type StoreBillingEntitlements = {
  paidFuelTruckSlots?: number
  modules?: Partial<Record<string, boolean>>
  slots?: Partial<Record<BillingSlotType, number>>
}

export type StoreUiSettings = {
  suppressAircraftSlotChargeReminder?: boolean
  showReportsTutorial?: boolean
}

export class Store extends FirebaseProperties {
  storeCode = ''
  config = ''
  modules: ModuleInfo[] = []
  part141Documents: DocI[] = []
  part141CertificateNumber = ''
  notifications: CustomNotification[] = []
  email = ''
  logo = ''
  address: Address = createAddress()
  name = ''
  aircraftCategoryReqs: CategoryReq[] = []
  dateApproachingThreshold = 7
  dateImminentThreshold = 1
  // Hours (Hobbs or Tach, whichever the aircraft bills on)
  // above which AircraftCheckLogReport.vue flags a logged flight as "High Flight Time"
  highFlightTimeThreshold = 5
  maintenanceNotifications: CustomNotification[] = []
  // Legacy persisted field; no longer limits the number of accounts.
  maxNumber = 100
  schedulingSettings: SchedulingSettings | null = null
  dispatch?: StoreDispatchPolicy
  billingStatus: BillingStatus = BillingStatus.Free
  bankAccounts: BankAccount[] = []

  membershipPrices: Record<PricedMembershipType, number> = {
    [membershipType.monthly]: 75,
    [membershipType.yearly]: 349,
    [membershipType.employee]: 0,
    [membershipType.other]: 0
  }

  /**
   * Members may pay and reactivate their own inactive accounts online. Off
   * unless the school turns it on (Store Settings); with it off, a member can
   * only send a reactivation request and the school's administrators decide.
   * Enforced by the server (functions/src/accounts/reactivation.ts).
   */
  onlineReactivation = false

  finance: {
    balance: number
  } = {
    balance: 0
  }
  billing?: StoreBillingInfo
  access?: { modules?: Partial<Record<string, boolean>> }
  settings?: StoreUiSettings
  maintenanceNotificationsCount: number = 0
  fuelReimbursementRate: Record<string, number> = {}

  /**
   * Work Orders pricing: shop labor rate, per-mechanic rates, the approval
   * limit, parts tax and default markup (Store Settings → Work Order Billing).
   */
  workOrderSettings: WorkOrderSettings = createWorkOrderSettings()

  get aircraftCollection(): CollectionReference<Aircraft> {
    return collection(
      db,
      FirestorePaths.nestedCollection(this.thisDocument, FirebaseCollections.Aircraft)
    ).withConverter(AircraftConverter)
  }

  get clientCollection(): CollectionReference<Client> {
    return collection(
      db,
      FirestorePaths.nestedCollection(this.thisDocument, FirebaseCollections.Client)
    ).withConverter(ClientConverter)
  }

  get eventsCollection(): CollectionReference<ScheduledEvent> {
    return collection(
      db,
      FirestorePaths.nestedCollection(this.thisDocument, FirebaseCollections.Events)
    ).withConverter(ScheduledEventConverter)
  }

  // get rentalsCollection(): CollectionReference<Rental> {
  //   return collection(
  //     db,
  //     FirestorePaths.nestedCollection(this.thisDocument, FirebaseCollections.Rentals)
  //   ).withConverter(RentalConverter)
  // }

  get roomsCollection(): CollectionReference<Room> {
    return collection(
      db,
      FirestorePaths.nestedCollection(this.thisDocument, FirebaseCollections.Rooms)
    ).withConverter(RoomConverter)
  }

  get leadsCollection(): CollectionReference<Lead> {
    return collection(
      db,
      FirestorePaths.nestedCollection(this.thisDocument, FirebaseCollections.Leads)
    ).withConverter(LeadConverter)
  }

  get equipmentCollection(): CollectionReference<Equipment> {
    return collection(
      db,
      FirestorePaths.nestedCollection(this.thisDocument, FirebaseCollections.Equipment)
    ).withConverter(EquipmentConverter)
  }

  get alertsCollection(): CollectionReference<StoreAlert> {
    return collection(
      db,
      FirestorePaths.nestedCollection(this.thisDocument, FirebaseCollections.Alerts)
    ).withConverter(StoreAlertConverter)
  }

  // TODO: Type this
  get financeLedgerCollection(): CollectionReference {
    return collection(
      db,
      FirestorePaths.nestedCollection(this.thisDocument, FirebaseCollections.Finance)
    )
  }

  get invoicesCollection(): CollectionReference {
    return collection(
      db,
      FirestorePaths.nestedCollection(this.thisDocument, FirebaseCollections.Invoices)
    )
  }

  constructor(data?: Partial<Store>) {
    super()
    Object.assign(this, data)

    this.finance = data?.finance ?? { balance: 0 }
    this.onlineReactivation = data?.onlineReactivation === true
    this.workOrderSettings = createWorkOrderSettings(data?.workOrderSettings)
    this.setId()
  }

  hasModule(moduleName: Module): boolean {
    if (
      this.access?.modules &&
      Object.prototype.hasOwnProperty.call(this.access.modules, moduleName)
    ) {
      return this.access.modules[moduleName] === true
    }
    return this.modules.some((m) => m.module === moduleName && m.hasAccess)
  }

  setId() {
    this.thisCollection = FirestorePaths.storeRoot()

    const id = this.firebaseInfo?.__id
    this.thisDocument = id ? FirestorePaths.store(id) : this.thisCollection
  }

  checkoutBlocking: CheckoutBlockingSettings = {
    blockOnAirworthiness: false,
    enforceELT: false,
    blockOn100Hr: false,
    blockOn50Hr: false
  }
}

export type BankAccount = {
  nickname: string // e.g. "Main Checking", "Fuel Account"
  bankName: string // e.g. "Chase", "Wells Fargo"
  accountNumber: string
  routingNumber: string
  qbAccountName: string // QuickBooks account name — leave blank if not used in QB
  isQBDefault: boolean
}

// Firestore data converter
export const StoreConverter = {
  toFirestore: (store: Store) => {
    const firebaseInfo = {
      ...store.firebaseInfo,
      _updatedAt: Timestamp.now()
    }

    return {
      storeCode: store.storeCode,
      config: store.config,
      modules: store.modules,
      part141Documents: store.part141Documents,
      part141CertificateNumber: store.part141CertificateNumber,
      notifications: store.notifications,
      email: store.email,
      logo: store.logo,
      address: store.address,
      name: store.name,
      finance: store.finance,
      membershipPrices: store.membershipPrices,
      onlineReactivation: store.onlineReactivation === true,
      aircraftCategoryReqs: store.aircraftCategoryReqs,
      dateApproachingThreshold: store.dateApproachingThreshold,
      dateImminentThreshold: store.dateImminentThreshold,
      highFlightTimeThreshold: store.highFlightTimeThreshold,
      maintenanceNotifications: store.maintenanceNotifications,
      maxNumber: store.maxNumber,
      maintenanceNotificationsCount: store.maintenanceNotifications.length,

      billingStatus: store.billingStatus,
      billing: store.billing ?? null,
      access: store.access ?? {},
      settings: store.settings ?? {},
      bankAccounts: store.bankAccounts ?? [],
      fuelReimbursementRate: store.fuelReimbursementRate ?? {},
      workOrderSettings: createWorkOrderSettings(store.workOrderSettings),

      dispatch: store.dispatch ?? null,

      checkoutBlocking: store.checkoutBlocking ?? {
        blockOnAirworthiness: false,
        enforceELT: false,
        blockOn50Hr: false
      },

      firebaseInfo
    }
  },
  fromFirestore: (
    snapshot: QueryDocumentSnapshot<DocumentData, DocumentData>,
    options?: SnapshotOptions
  ) => {
    const data = snapshot.data(options)
    data.bankAccounts = data.bankAccounts ?? []

    return new Store(convertTimestampsToDates(data))
  }
}

export type StoreDispatchPolicy = {
  part141?: boolean
  part61?: boolean
}

export type CheckoutBlockingSettings = {
  blockOnAirworthiness: boolean // Annual, Pitot/Static, Transponder, Registration
  enforceELT: boolean // ELT Inspection + ELT Battery, together
  blockOn50Hr: boolean // separate from the above, per original design
  blockOn100Hr: boolean // 100-hour inspection (also gated per-aircraft by do100Hr)
}
